import { AxiosError } from "axios";
import { http } from "@/lib/http";
import { withIdempotencyKey } from "@/lib/idempotency";
import { isMockEnabled } from "@/lib/mock-flags";
import * as mock from "../mocks/fixtures";
import type {
  AgencyAssignment,
  AgencyProperty,
  CreateAgencyPropertyInput,
  DistributeInput,
  PropertyCycle,
} from "../mocks/fixtures";

/**
 * Recusa vinda do crm-api (via agency-api). Diferente da leitura de produção, cadastrar e
 * distribuir são ESCRITAS: a imobiliária precisa saber o motivo, não um erro genérico.
 */
export class PropertyActionError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "PropertyActionError";
  }
}

function toActionError(error: unknown): never {
  if (error instanceof AxiosError && error.response) {
    const body = error.response.data as { code?: string; message?: string } | undefined;
    if (body?.code) throw new PropertyActionError(body.code, body.message ?? "");
  }
  throw error;
}

export const listProperties = async (): Promise<AgencyProperty[]> => {
  if (isMockEnabled("agency")) return mock.listProperties();
  const { data } = await http.get<AgencyProperty[]>("/properties");
  return data;
};

export const createProperty = async (
  input: CreateAgencyPropertyInput,
  // A chave vem de fora: quem repete a operação (retry do usuário) precisa reenviar a MESMA.
  // Gerar aqui, a cada chamada, seria o mesmo que não mandar chave nenhuma.
  idempotencyKey?: string,
): Promise<AgencyProperty> => {
  if (isMockEnabled("agency")) return mock.createProperty(input);
  try {
    const { data } = await http.post<AgencyProperty>(
      "/properties",
      input,
      withIdempotencyKey(idempotencyKey),
    );
    return data;
  } catch (error) {
    toActionError(error);
  }
};

export const distributeProperty = async (
  propertyId: string,
  input: DistributeInput,
  idempotencyKey?: string,
): Promise<AgencyAssignment[]> => {
  if (isMockEnabled("agency")) return mock.distributeProperty(propertyId, input);
  try {
    const { data } = await http.post<AgencyAssignment[]>(
      `/properties/${propertyId}/distribute`,
      input,
      withIdempotencyKey(idempotencyKey),
    );
    return data;
  } catch (error) {
    toActionError(error);
  }
};

export const getPropertyCycle = async (propertyId: string): Promise<PropertyCycle> => {
  if (isMockEnabled("agency")) return mock.getPropertyCycle(propertyId);
  const { data } = await http.get<PropertyCycle>(`/properties/${propertyId}/cycle`);
  return data;
};

export const revokeAssignment = async (assignmentId: string): Promise<void> => {
  if (isMockEnabled("agency")) return mock.revokeAssignment(assignmentId);
  try {
    await http.delete(`/properties/assignments/${assignmentId}`);
  } catch (error) {
    toActionError(error);
  }
};

export type {
  AgencyAssignment,
  AgencyProperty,
  AssignmentMode,
  CreateAgencyPropertyInput,
  CycleContract,
  CycleDeal,
  CycleInspection,
  CycleProposal,
  DistributeInput,
  PropertyActivity,
  PropertyCycle,
} from "../mocks/fixtures";
